import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Check, X } from 'lucide-react';
import { Button } from './ui/button';

import { API } from '../lib/api';

export default function OnboardingChecklist() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/onboarding`);
        setData(res.data);
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  if (!data || data.completed || dismissed) return null;

  const doneCount = data.steps.filter((s) => s.done).length;
  const nextStep = data.steps.find((s) => !s.done);

  const dismiss = async () => {
    try {
      await axios.post(`${API}/auth/onboarding/complete`);
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <section aria-labelledby="onboarding-title" className="flex flex-col gap-4 rounded-md border border-line bg-surface p-5" data-testid="onboarding-checklist">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="m-0 text-xs font-bold uppercase leading-4 tracking-[0.08em] text-ink-muted">Getting started</p>
          <h2 id="onboarding-title" className="m-0 mt-1 font-display text-[22px] font-bold leading-7">
            {data.all_done ? "You're all set" : `${doneCount} of ${data.steps.length} steps complete`}
          </h2>
          {data.demo_inspection_id && (
            <p className="m-0 mt-1 text-ink-muted">
              Explore your{' '}
              <Link to={`/inspections/${data.demo_inspection_id}`} className="font-bold text-ink underline decoration-marker decoration-2 underline-offset-[3px]">
                demo inspection
              </Link>{' '}
              while you set up your portfolio.
            </p>
          )}
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={dismiss} aria-label="Hide checklist" className="h-9 w-9 text-ink-muted">
          <X strokeWidth={1.5} />
        </Button>
      </div>

      <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${data.steps.length}, minmax(0, 1fr))` }} aria-hidden="true">
        {data.steps.map((s) => (
          <span key={s.id} className={`h-1.5 border ${s.done ? 'border-sev-low bg-sev-low' : 'border-line bg-sunken'}`} />
        ))}
      </div>

      <ol className="m-0 flex list-none flex-col gap-0.5 p-0">
        {data.steps.map((step) => {
          const isNext = nextStep && step.id === nextStep.id;
          return (
            <li key={step.id}>
              <Link
                to={step.link}
                className="flex min-h-11 items-center gap-3 rounded-md px-2 text-ink no-underline hover:bg-sunken"
              >
                <span
                  className={`inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-sm border-2 ${
                    step.done ? 'border-sev-low bg-sev-low text-surface' : 'border-line-strong'
                  }`}
                  aria-hidden="true"
                >
                  {step.done && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                </span>
                <span className={step.done ? 'text-ink-muted line-through' : isNext ? 'font-bold' : ''}>
                  {step.label}
                  <span className="sr-only">{step.done ? ' (done)' : ''}</span>
                </span>
                {isNext && <ArrowRight className="ml-auto h-5 w-5 text-ink-muted" strokeWidth={1.5} aria-hidden="true" />}
              </Link>
            </li>
          );
        })}
      </ol>

      {data.all_done && (
        <Button onClick={dismiss} variant="outline" className="self-start">Hide checklist</Button>
      )}
    </section>
  );
}
